import { Dispatch, FC, ReactNode, RefObject, SetStateAction, createContext, createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { TypeOfAvailableVirtualVisitLinkTypes, useGalleryConstants } from './GalleryConstants.context'

import { AssignmentDTOIsAdministratorDTO } from 'utils/typeguards'
import moment from 'moment-timezone'
import { useAuth0 } from 'utils/auth'
import { escapeHtmlAttribute } from 'utils/product/virtualVisitUrl'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryProduct } from './GalleryProduct.context'
import { useGalleryVisualsMeta } from './GalleryVisualsMeta.context'

interface GalleryVirtualVisitInterface {
  virtualVisitShareLinkRef: RefObject<HTMLInputElement>
  virtualVisitEmbedCodeRef: RefObject<HTMLInputElement>
  selectedVirtualVisitLinkType: TypeOfAvailableVirtualVisitLinkTypes
  setSelectedVirtualVisitLinkType: Dispatch<SetStateAction<TypeOfAvailableVirtualVisitLinkTypes>>
  virtualVisitInput: string
  setVirtualVisitInput: Dispatch<SetStateAction<string>>
  virtualVisitURL: string
  generateVirtualVisitShareLink: (virtualVisitURL: string, linkType: TypeOfAvailableVirtualVisitLinkTypes) => string
  virtualVisitShareLink: string
  virtualVisitExpirationDate: string | null
  virtualVisitExpired: boolean
  virtualVisitMatterportEmail?: string
  virtualVisitEmbedCode: string
}

const defaultGalleryVirtualVisitValue: GalleryVirtualVisitInterface = {
  virtualVisitShareLinkRef: createRef(),
  virtualVisitEmbedCodeRef: createRef(),
  selectedVirtualVisitLinkType: 'BRANDED',
  setSelectedVirtualVisitLinkType: () => { throw new Error('setSelectedVirtualVisitLinkType is undefined') },
  virtualVisitInput: '',
  setVirtualVisitInput: () => { throw new Error('setVirtualVisitInput is undefined') },
  virtualVisitURL: '',
  generateVirtualVisitShareLink: () => { throw new Error('generateVirtualVisitShareLink is undefined') },
  virtualVisitShareLink: '',
  virtualVisitExpirationDate: null,
  virtualVisitExpired: false,
  virtualVisitEmbedCode: '',
}

/** Gallery virtual visit context */
export const GalleryVirtualVisitContext = createContext<GalleryVirtualVisitInterface>(defaultGalleryVirtualVisitValue)
/** Gallery virtual visit context hook */
export const useGalleryVirtualVisit = (): GalleryVirtualVisitInterface => useContext(GalleryVirtualVisitContext)

/** Context provider for gallery virtual visit */
export const GalleryVirtualVisitContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {

    const { roles } = useAuth0()

    const { AvailableVirtualVisitLinkTypes } = useGalleryConstants()
    const { allVisualsData, listVisualsQuery } = useGalleryVisualsMeta()
    const { assignmentData } = useGalleryAssignment()
    const { isVirtualVisitFloorfy } = useGalleryProduct()

    // A Floorfy tour is delivered as the CT submitted it, so the admin's input starts from the CT's link.
    // With several CT links there is no safe pick: the admin chooses.
    const ctFloorfyLink = useMemo(() => {
      if (!isVirtualVisitFloorfy || !assignmentData || !AssignmentDTOIsAdministratorDTO(assignmentData)) return ''
      const links = assignmentData.virtualVisitUrls ?? []
      return links.length === 1 ? links[0] : ''
    }, [isVirtualVisitFloorfy, assignmentData])

    const virtualVisitShareLinkRef = useRef<HTMLInputElement>(null)
    const virtualVisitEmbedCodeRef = useRef<HTMLInputElement>(null)
    const [selectedVirtualVisitLinkType, setSelectedVirtualVisitLinkType] = useState<TypeOfAvailableVirtualVisitLinkTypes>(AvailableVirtualVisitLinkTypes.BRANDED)
    const [virtualVisitInput, setVirtualVisitInput] = useState('')
    const [virtualVisitURL, setVirtualVisitURL] = useState('')
    const generateVirtualVisitShareLink = useCallback((vvURL: string, linkType: TypeOfAvailableVirtualVisitLinkTypes) => {
      if (!vvURL) return ''
      const isMatterportURL = vvURL.includes('my.matterport.com')
      // Only append Matterport-specific params for standard Matterport URLs
      if (isMatterportURL) {
        // Use this link to learn about Matterport params https://support.matterport.com/s/article/URL-Parameters?language=en_US
        const separator = vvURL.includes('?') ? '&' : '?'
        switch (linkType) {
          case AvailableVirtualVisitLinkTypes.UNBRANDED:
            return `${vvURL}${separator}brand=0`
          case AvailableVirtualVisitLinkTypes.MLS_READY:
            return `${vvURL}${separator}mls=${roles.isClient ? '2' : '1'}`
          default:
            return vvURL
        }
      }
      // Custom URLs (e.g. Treedis) are returned as-is
      return vvURL
    }, [AvailableVirtualVisitLinkTypes, roles])
    const virtualVisitShareLink = useMemo(() => generateVirtualVisitShareLink(virtualVisitURL, selectedVirtualVisitLinkType), [generateVirtualVisitShareLink, virtualVisitURL, selectedVirtualVisitLinkType])
    const virtualVisitExpirationDate = useMemo(() => allVisualsData?.virtualVisit?.expirationDate || null, [allVisualsData])
    const virtualVisitExpired = useMemo(() => allVisualsData?.virtualVisit?.expirationDate ? moment(allVisualsData.virtualVisit.expirationDate).isBefore(moment()) : false, [allVisualsData])
    const virtualVisitMatterportEmail = useMemo(() => allVisualsData?.virtualVisit?.matterportEmail, [allVisualsData])
    const virtualVisitEmbedCode = useMemo(() => `<iframe width="853" height="480" src="${escapeHtmlAttribute(virtualVisitShareLink)}" frameBorder="0" allowFullScreen allow="xr-spatial-tracking"></iframe>`, [virtualVisitShareLink])

    // Update virtual visit URL and input when visual list changes
    useEffect(() => {
      if (!allVisualsData || !listVisualsQuery.isSuccess) return

      const virtualVisit = allVisualsData.virtualVisit

      const url = virtualVisit?.matterportUrl || ''
      // A late CT link must not overwrite what the admin already typed.
      setVirtualVisitInput(prev => url || (ctFloorfyLink ? prev || ctFloorfyLink : ''))
      setVirtualVisitURL(url)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allVisualsData, ctFloorfyLink])

    return (
      <GalleryVirtualVisitContext.Provider
        value={{
          virtualVisitShareLinkRef,
          virtualVisitEmbedCodeRef,
          selectedVirtualVisitLinkType,
          setSelectedVirtualVisitLinkType,
          virtualVisitInput,
          setVirtualVisitInput,
          virtualVisitURL,
          generateVirtualVisitShareLink,
          virtualVisitShareLink,
          virtualVisitExpirationDate,
          virtualVisitExpired,
          virtualVisitMatterportEmail,
          virtualVisitEmbedCode,
        }}
      >
        {children}
      </GalleryVirtualVisitContext.Provider>
    )
  }
